import React from "react";
import { Box, HStack, Text, VStack } from "@chakra-ui/react";

export interface DualColorMapLegendProps {
  rainGradient: string; // CSS linear-gradient string
  snowGradient: string; // CSS linear-gradient string
  rainLabel: string; // "Rain" / "Chuva"
  snowLabel: string; // "Snow" / "Neve"
  rainScaleLabels?: Array<{ label: string; position: number }>; // Intensity scale labels
  rainMmhLabels?: Array<{ label: string; position: number }>; // mm/h values on gradient
  width?: number;
}

/**
 * DualColorMapLegend - Displays rain and snow precipitation colormaps
 * using continuous CSS gradients with visual labels.
 *
 * Uses the full 128-color Rainbow AI table interpolated into smooth gradients
 * similar to Windy's colormap style.
 *
 * Rain colormap is displayed at 2x height (40px) to be more prominent,
 * with mm/h values displayed on the gradient bar and intensity scale labels
 * (Light/Moderate/Heavy) positioned below.
 * Snow colormap is displayed at 1x height (20px) below the rain colormap.
 *
 * Each gradient bar has a label (Rain/Snow) positioned beside it.
 *
 * Used by RadarColorMap component to display dual precipitation type legend.
 */
const DualColorMapLegend: React.FC<DualColorMapLegendProps> = ({
  rainGradient,
  snowGradient,
  rainLabel,
  snowLabel,
  rainScaleLabels,
  rainMmhLabels,
  width = 300,
}) => {
  return (
    <VStack spacing={1} align="stretch" width={`${width}px`}>
      {/* Rain colormap with label and scale labels */}
      <VStack spacing={0} width="100%" align="stretch">
        <HStack spacing={2} width="100%">
          <Text
            fontSize="xs"
            fontWeight="bold"
            minW="40px"
            color="white"
            textShadow="1px 1px 2px rgba(0, 0, 0, 0.8)"
            userSelect="none"
          >
            {rainLabel}
          </Text>
          <Box
            flex={1}
            height="30px"
            background={rainGradient}
            borderRadius="4px"
            border="1px solid rgba(255, 255, 255, 0.2)"
            position="relative"
          >
            {/* mm/h values displayed on the gradient bar */}
            {rainMmhLabels?.map((item, index) => (
              <Text
                key={index}
                position="absolute"
                left={`${item.position}%`}
                top="50%"
                transform="translate(-50%, -50%)"
                fontSize="9px"
                fontWeight="bold"
                color="white"
                textShadow="0 0 3px rgba(0, 0, 0, 1), 1px 1px 2px rgba(0, 0, 0, 0.9)"
                userSelect="none"
                whiteSpace="nowrap"
              >
                {item.label}
              </Text>
            ))}
          </Box>
        </HStack>

        {/* Scale labels below gradient */}
        {rainScaleLabels && rainScaleLabels.length > 0 && (
          <HStack spacing={2} width="100%">
            <Box minW="40px" /> {/* Spacer to align with gradient */}
            <Box flex={1} position="relative" height="18px">
              {rainScaleLabels.map((item, index) => (
                <Text
                  key={index}
                  position="absolute"
                  left={`${item.position}%`}
                  transform="translateX(-50%)"
                  fontSize="10px"
                  color="white"
                  textShadow="1px 1px 2px rgba(0, 0, 0, 0.8)"
                  userSelect="none"
                  whiteSpace="nowrap"
                >
                  {item.label}
                </Text>
              ))}
            </Box>
          </HStack>
        )}
      </VStack>

      {/* Snow colormap with label */}
      <HStack spacing={2} width="100%">
        <Text
          fontSize="xs"
          fontWeight="medium"
          minW="40px"
          color="white"
          textShadow="1px 1px 2px rgba(0, 0, 0, 0.8)"
          userSelect="none"
        >
          {snowLabel}
        </Text>
        <Box
          flex={1}
          height="20px"
          background={snowGradient}
          borderRadius="4px"
          border="1px solid rgba(255, 255, 255, 0.2)"
        />
      </HStack>
    </VStack>
  );
};

export default DualColorMapLegend;
